/**
 * Ensures only one browser tab of the app is "active" at a time.
 *
 * Every tab claims active status as soon as it loads by broadcasting on a shared
 * BroadcastChannel. Any tab that receives a claim from another tab id deactivates
 * itself (closes its websocket, shows a "session expired" view). The newest tab
 * to load/refresh always wins; a deactivated tab only becomes active again on refresh.
 */
const CHANNEL_NAME = "app_tab_coordination";

const TAB_ID: string =
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

type CoordinationMessage = {
  type: "CLAIM_ACTIVE";
  tabId: string;
};

class TabCoordinationService {
  private channel: BroadcastChannel | null = null;
  private deactivated = false;

  public init(onDeactivated: () => void): void {
    if (typeof BroadcastChannel === "undefined" || this.channel) {
      return;
    }

    this.channel = new BroadcastChannel(CHANNEL_NAME);

    this.channel.onmessage = (event: MessageEvent<CoordinationMessage>) => {
      const data = event.data;
      if (!data || data.tabId === TAB_ID || data.type !== "CLAIM_ACTIVE") {
        return;
      }

      if (!this.deactivated) {
        this.deactivated = true;
        onDeactivated();
      }
    };

    const claim: CoordinationMessage = { type: "CLAIM_ACTIVE", tabId: TAB_ID };
    this.channel.postMessage(claim);
  }

  public destroy(): void {
    this.channel?.close();
    this.channel = null;
  }
}

export const tabCoordinationService = new TabCoordinationService();
